import { useCallback } from 'react';
import { usePluginFeatures } from '../../../shared/hooks/usePluginFeatures';
import { useDeleteExportMetadataForAssetMutation } from '../../gql/mutations/generated/DeleteExportMetadataForAsset';
import type { RemoveExportMetadataMutationOptions } from '../../gql/mutations/generated/RemoveExportMetadata';
import { useRemoveExportMetadataMutation } from '../../gql/mutations/generated/RemoveExportMetadata';

/**
 * Hook that provides a function to remove export metadata
 * for an asset and sequence
 */
export const useRemoveExportMetadata = () => {
  const { canRemoveExportMetadata } = usePluginFeatures();
  const [removeExportMetadata] = useRemoveExportMetadataMutation({
    context: {
      resolveWithUXP: true,
    },
  });

  const [deleteExportMetadataForAsset] =
    useDeleteExportMetadataForAssetMutation({
      context: {
        resolveWithUXP: true,
      },
    });

  return useCallback(
    async (options: {
      variables: RemoveExportMetadataMutationOptions['variables'];
      refetchQueries?: string[];
      onCompleted?: () => void | Promise<void>;
    }) => {
      let removedSequences;
      if (canRemoveExportMetadata) {
        const { data } = await removeExportMetadata(options);
        if (data?.removeExportMetadata) {
          removedSequences = data.removeExportMetadata;
        }
      } else {
        const { data } = await deleteExportMetadataForAsset(options);
        if (data?.deleteExportMetadataForAsset) {
          removedSequences = [data.deleteExportMetadataForAsset];
        }
      }

      return removedSequences ?? [];
    },
    [
      canRemoveExportMetadata,
      removeExportMetadata,
      deleteExportMetadataForAsset,
    ]
  );
};
